import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/lib/apiUrl';

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: '', description: '', taux_horaire: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/services`);
      setServices(data.services || []);
    } catch (e) {
      toast.error('Erreur chargement des services');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ nom: '', description: '', taux_horaire: 0 });
    setShowDialog(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ nom: s.nom, description: s.description || '', taux_horaire: s.taux_horaire || 0 });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/services/${editing.id}`, form);
      } else {
        await axios.post(`${API_URL}/api/services`, form);
      }
      toast.success(editing ? 'Service modifie' : 'Service cree');
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleToggle = async (s) => {
    try {
      await axios.put(`${API_URL}/api/services/${s.id}`, { actif: !s.actif });
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le service "${s.nom}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/services/${s.id}`);
      toast.success('Service supprime');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-blue-600" /> Services
          </h1>
          <p className="text-gray-500 text-sm mt-1">Catalogue de services pour les soumissions et factures</p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-service">
          <Plus className="w-4 h-4 mr-2" /> Nouveau service
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>Aucun service. Creez votre premier service.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="p-3 text-left font-semibold">Nom</th>
                  <th className="p-3 text-left font-semibold">Description</th>
                  <th className="p-3 text-right font-semibold">Taux horaire</th>
                  <th className="p-3 text-center font-semibold">Statut</th>
                  <th className="p-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id} className="border-b hover:bg-gray-50/50 transition-colors" data-testid={`service-row-${s.id}`}>
                    <td className="p-3 font-medium">{s.nom}</td>
                    <td className="p-3 text-gray-600 max-w-xs truncate">{s.description || '—'}</td>
                    <td className="p-3 text-right">{fmt(s.taux_horaire)} $/h</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch checked={s.actif} onCheckedChange={() => handleToggle(s)} />
                        <Badge className={s.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {s.actif ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`btn-edit-${s.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(s)} data-testid={`btn-delete-${s.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Dialog creation/edition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le service' : 'Nouveau service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nom du service *</Label>
              <Input
                value={form.nom}
                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                placeholder="Ex: Tirage de joints"
                data-testid="input-service-nom"
              />
            </div>
            <div>
              <Label>Description par defaut</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Description qui apparaitra sur la soumission/facture"
                rows={3}
                data-testid="input-service-description"
              />
            </div>
            <div>
              <Label>Taux horaire par defaut ($/h)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.taux_horaire}
                onChange={e => setForm(f => ({ ...f, taux_horaire: parseFloat(e.target.value) || 0 }))}
                data-testid="input-service-taux"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="btn-save-service">
              {editing ? 'Enregistrer' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
